import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { type CountryConfig, HIDDEN_COUNTRY_SLUGS, PHONE_LOCAL_LENGTHS, STATIC_COUNTRIES } from "@/config/countries";

const COUNTRIES_QUERY_KEY = ["countries"];

async function fetchCountries(): Promise<CountryConfig[]> {
  const { data, error } = await supabase
    .from("countries")
    .select("*")
    .order("name");

  if (error) throw error;

  const filtered = (data ?? []).filter((row) => !HIDDEN_COUNTRY_SLUGS.includes(row.slug));

  return filtered.map((row) => ({
    id: row.id,
    slug: row.slug,
    code: row.country_code,
    name: row.name,
    defaultLanguage: row.default_language as "fr" | "en",
    currency: row.currency,
    currencySymbol: row.currency_symbol,
    whatsappNumber: row.whatsapp_number ?? "",
    customerCarePhone: row.customer_care_phone ?? "",
    timezone: row.timezone,
    flag: (row as any).flag || "",
    preposition: ((row as any).preposition || "au") as "au" | "en",
    ...(PHONE_LOCAL_LENGTHS[row.slug] !== undefined ? { expectedLocalPhoneLength: PHONE_LOCAL_LENGTHS[row.slug] } : {}),
  }));
}

export function useCountries() {
  // initialData = fallback statique → 1er render synchrone, pas de blocage LCP.
  // La query Supabase tourne en arrière-plan et remplace silencieusement les
  // valeurs si le DB diffère.
  const { data: countries = STATIC_COUNTRIES, isLoading } = useQuery({
    queryKey: COUNTRIES_QUERY_KEY,
    queryFn: fetchCountries,
    staleTime: Infinity,
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    initialData: STATIC_COUNTRIES,
  });

  return { countries, isLoading };
}

export function getCountryBySlugFromList(countries: CountryConfig[], slug: string) {
  return countries.find((c) => c.slug === slug);
}

export function getCountryByCodeFromList(countries: CountryConfig[], code: string) {
  return countries.find((c) => c.code === code);
}
